import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PublicShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function Signup() {
  const { register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    password: "",
    confirm: "",
    account_type: "individual",
    business_name: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (!form.email && !form.phone) {
      toast.error("Provide an email or cellphone");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        full_name: form.full_name,
        email: form.email || null,
        phone: form.phone || null,
        password: form.password,
        account_type: form.account_type,
        business_name: form.account_type === "business" ? form.business_name : null,
      };
      const user = await register(payload);
      toast.success(`Welcome, ${user.full_name}!`);
      navigate("/dashboard/kyc");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicShell>
      <section className="max-w-2xl mx-auto px-5 sm:px-8 py-16">
        <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#00C4B4]">Merchant signup</p>
        <h1 className="font-display mt-2 text-4xl tracking-tight font-bold text-[#0A2540]">Become a FlexiVouch merchant.</h1>
        <p className="mt-3 text-[#666666]">Issue stored-value vouchers in minutes. KYC required before voucher generation.</p>

        <form onSubmit={submit} data-testid="signup-form" className="mt-8 bg-white border border-[#E5E7EB] rounded-2xl p-7 space-y-5">
          <div className="space-y-3">
            <Label>Account type</Label>
            <RadioGroup
              value={form.account_type}
              onValueChange={(v) => setForm({ ...form, account_type: v })}
              className="grid grid-cols-2 gap-3"
            >
              <label htmlFor="ind" data-testid="signup-type-individual" className={`flex items-center gap-3 border rounded-lg p-4 cursor-pointer ${form.account_type === "individual" ? "border-[#00C4B4] bg-[#00C4B4]/5" : "border-[#E5E7EB]"}`}>
                <RadioGroupItem value="individual" id="ind" />
                <span className="font-medium text-[#0A2540]">Individual</span>
              </label>
              <label htmlFor="biz" data-testid="signup-type-business" className={`flex items-center gap-3 border rounded-lg p-4 cursor-pointer ${form.account_type === "business" ? "border-[#00C4B4] bg-[#00C4B4]/5" : "border-[#E5E7EB]"}`}>
                <RadioGroupItem value="business" id="biz" />
                <span className="font-medium text-[#0A2540]">Business (Pty Ltd / CC)</span>
              </label>
            </RadioGroup>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="fn">Full name</Label>
              <Input data-testid="signup-fullname-input" id="fn" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            {form.account_type === "business" && (
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="bn">Business name</Label>
                <Input data-testid="signup-business-name-input" id="bn" required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="em">Email</Label>
              <Input data-testid="signup-email-input" id="em" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ph">SA Cellphone</Label>
              <Input data-testid="signup-phone-input" id="ph" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="082 123 4567" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw">Password</Label>
              <Input data-testid="signup-password-input" id="pw" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cf">Confirm password</Label>
              <Input data-testid="signup-confirm-input" id="cf" type="password" required minLength={8} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
            </div>
          </div>
          <p className="text-xs text-[#666666]">
            Password must contain at least 8 characters, including uppercase, lowercase, a number and a special character.
          </p>
          <Button data-testid="signup-submit-btn" disabled={busy} type="submit" className="w-full bg-[#0A2540] hover:bg-[#133D6B] text-white">
            {busy ? "Creating account…" : "Create merchant account"}
          </Button>
          <p className="text-sm text-[#666666] text-center">
            Already registered?{" "}
            <Link to="/login" data-testid="signup-login-link" className="text-[#00C4B4] font-semibold hover:underline">Sign in</Link>
          </p>
        </form>
      </section>
    </PublicShell>
  );
}
